import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Users } from 'lucide-react';
import type { MarketDemandResponse } from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { InfoTooltip } from './InfoTooltip';

/**
 * Market-demand tile (SIL-573 PR 2 of 2) — last element of the Outlook band
 * region (asset-financials.tsx), own component per the plan's "do not
 * bloat OutlookBand.tsx." Consumes `GET
 * /api/client/financials/market-demand` (`loadMarketDemand`,
 * market-demand.ts).
 *
 * "Demand for what you hold" — NOT "offers you can accept." This is a
 * DIFFERENT statistic than `/opportunities` (price-filtered, offerable-
 * lifecycle-only): matches on SKU id alone, any price, every lifecycle.
 * Deliberately has no link out — `/opportunities` is a strictly SMALLER set
 * than this tile's number, so a link there would visibly under-deliver.
 *
 * Every count/quantity here is already aggregated + k-anonymity-floor-gated
 * server-side (`market-demand.ts` module header) — this component never
 * receives, and therefore can never render, a per-buyer or per-offer row.
 *
 * EXPORT: like OutlookBand, never wired into any CSV/PDF export path —
 * asset-financials.tsx passes no `exportCsv` to `FinancialsReportScreen`,
 * and this component must stay that way.
 */
export function MarketDemandTile() {
  const demandQ = useQuery<MarketDemandResponse>({
    queryKey: qk.financials.marketDemand(),
    queryFn: async () => {
      const res = await api.api.client.financials['market-demand'].$get();
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      return (await res.json()) as unknown as MarketDemandResponse;
    },
  });

  if (demandQ.isLoading) {
    return (
      <Card padding="md">
        <div className="text-sm text-muted-foreground">Loading market demand…</div>
      </Card>
    );
  }
  if (demandQ.error || !demandQ.data) {
    return null; // non-fatal — the rest of the band still renders.
  }

  const data = demandQ.data;
  const hasDemand = data.buyerCount > 0 || data.totalQuantityDesired > 0 || data.matchedSkuCount > 0;

  return (
    <Card padding="md">
      <div className="flex items-start gap-2.5">
        <Users className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          {hasDemand ? (
            <div className="flex flex-wrap items-center gap-1.5 text-sm text-foreground">
              <span>
                <span className="font-mono font-semibold">{data.buyerCount}</span> buyer
                {data.buyerCount === 1 ? '' : 's'} seeking{' '}
                <span className="font-mono font-semibold">{data.totalQuantityDesired}</span> unit
                {data.totalQuantityDesired === 1 ? '' : 's'} across{' '}
                <span className="font-mono font-semibold">{data.matchedSkuCount}</span> of your SKU
                {data.matchedSkuCount === 1 ? '' : 's'}
              </span>
              {data.topDemand && (
                <InfoTooltip label="What's most in demand">
                  Most in demand: {data.topDemand.label} — {data.topDemand.buyerCount} buyer
                  {data.topDemand.buyerCount === 1 ? '' : 's'}, {data.topDemand.quantityDesired} unit
                  {data.topDemand.quantityDesired === 1 ? '' : 's'}
                </InfoTooltip>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No open buyer demand found for what you currently hold.
            </p>
          )}
          {!data.offersEnabled && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Offers are currently disabled for your org ·{' '}
              <Link to="/profile" className="text-brand-600 hover:text-brand-700 hover:underline">
                Enable in Settings
              </Link>
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
